/**
 * Path utilities for handling navigation in auth flows.
 * Ensures proper path resolution when auth pages are served under a base path (e.g., /auth/)
 */

/**
 * Resolves an auth route path relative to the current location.
 * If the current path is under /auth/, it will preserve that prefix.
 * Otherwise, it returns the path as-is.
 *
 * @param targetPath - The target auth path (e.g., '/sign-in', '/sign-up')
 * @returns The resolved path with proper base path handling
 *
 * @example
 * // Current URL: http://localhost:5174/auth/sign-in
 * resolveAuthPath('/sign-up') // Returns '/auth/sign-up'
 *
 * @example
 * // Current URL: http://localhost:5174/sign-in
 * resolveAuthPath('/sign-up') // Returns '/sign-up'
 */
export function resolveAuthPath(targetPath: string): string {
  if (typeof window === 'undefined') {
    return targetPath;
  }

  const currentPath = window.location.pathname;

  // Check if we're currently under /auth/ base path
  if (currentPath.startsWith('/auth/')) {
    // If target already has /auth/ prefix, return as-is
    if (targetPath.startsWith('/auth/')) {
      return targetPath;
    }
    // Add /auth/ prefix to the target path
    return `/auth${targetPath}`;
  }

  // Not under /auth/, return target path as-is
  return targetPath;
}

/**
 * Resolves an auth route URL with search params preserved.
 * Similar to resolveAuthPath but returns a full URL string with query parameters.
 *
 * @param targetPath - The target auth path (e.g., '/sign-in', '/sign-up')
 * @param searchParams - Optional URLSearchParams to append
 * @returns The resolved URL as a string
 *
 * @example
 * // Current URL: http://localhost:5174/auth/sign-in?redirect=...
 * resolveAuthUrl('/sign-up', searchParams) // Returns '/auth/sign-up?redirect=...'
 */
export function resolveAuthUrl(targetPath: string, searchParams?: URLSearchParams): string {
  const resolvedPath = resolveAuthPath(targetPath);

  if (!searchParams || searchParams.toString() === '') {
    return resolvedPath;
  }

  return `${resolvedPath}?${searchParams.toString()}`;
}
